/**
 * <s-slider data-autoplay="5000"> — one-slide-at-a-time carousel (announcement bar, homepage hero).
 *
 * Markup contract (all inside the element):
 * - [data-slider-track]  overflow-x scroll-snap container; each child is one full-width slide.
 *                        Slides hidden by CSS at the current breakpoint are skipped.
 * - [data-slider-prev] / [data-slider-next]  optional buttons; they wrap around.
 * - [data-slider-dots]   optional empty container; one dot button per visible slide is built in it.
 * - data-autoplay        ms between slides; 0 or missing = no autoplay. Pauses on hover, focus,
 *                        touch and when the tab is hidden; off for prefers-reduced-motion.
 * Sets `data-multiple` on itself when more than one slide is visible, so controls can hide otherwise.
 * Dots and the current slide are exposed with aria-current; slides get aria-hidden + inert when off-screen.
 */
class SSlider extends HTMLElement {
  #ac;
  #io;
  #ro;
  #timer = 0;
  #paused = false;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    this.dots = this.querySelector('[data-slider-dots]');

    this.prev?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.next?.addEventListener('click', () => this.go(this.#index + 1), { signal });

    const pause = () => {
      this.#paused = true;
      this.stop();
    };
    const resume = () => {
      this.#paused = false;
      this.start();
    };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    this.track.addEventListener('touchstart', pause, { passive: true, signal });
    this.track.addEventListener('touchend', resume, { passive: true, signal });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()), { signal });

    this.#io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const i = this.slides.indexOf(entry.target);
            if (i >= 0) this.setCurrent(i);
          }
        });
      },
      { root: this.track, threshold: [0.6] },
    );
    this.#ro = new ResizeObserver(() => this.refresh());
    this.#ro.observe(this.track);
    this.refresh();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    this.#ro?.disconnect();
    this.stop();
  }

  /** Re-reads which slides are visible at this breakpoint and rebuilds dots. */
  refresh() {
    const visible = [...this.track.children].filter((el) => el.offsetWidth > 0);
    const changed = !this.slides || visible.length !== this.slides.length || visible.some((el, i) => el !== this.slides[i]);
    if (!changed) return;
    this.#io.disconnect();
    this.slides = visible;
    this.slides.forEach((slide) => this.#io.observe(slide));
    this.toggleAttribute('data-multiple', this.slides.length > 1);

    if (this.dots) {
      this.dots.replaceChildren(
        ...this.slides.map((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.sliderDot = '';
          dot.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
          dot.addEventListener('click', () => this.go(i));
          return dot;
        }),
      );
    }
    this.#index = 0;
    this.track.scrollLeft = 0;
    this.setCurrent(0);
    this.start();
  }

  setCurrent(i) {
    this.#index = i;
    this.slides.forEach((slide, n) => {
      const active = n === i;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots?.querySelectorAll('[data-slider-dot]').forEach((dot, n) => dot.setAttribute('aria-current', String(n === i)));
  }

  go(i) {
    if (!this.slides?.length) return;
    const count = this.slides.length;
    const target = ((i % count) + count) % count;
    const slide = this.slides[target];
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const left = rtl ? slide.offsetLeft + slide.offsetWidth - this.track.clientWidth : slide.offsetLeft;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.setCurrent(target);
    this.start();
  }

  start() {
    this.stop();
    const delay = Number(this.dataset.autoplay) || 0;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!delay || reduce || this.#paused || document.hidden || !this.slides || this.slides.length < 2) return;
    this.#timer = window.setTimeout(() => this.go(this.#index + 1), delay);
  }

  stop() {
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }
}

if (!customElements.get('s-slider')) customElements.define('s-slider', SSlider);
